import withRouter from "Components/Common/withRouter"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import React from "react"
import { IProjectQueryParam, ProjectForecast, } from "models/project"
import { syncProjects, getProjectForecast, updateProjectFinance, downloadProjectForecastCSV, getMostRecentSyncLog } from "slices/thunk"
import { getProjectForecastSearchFields } from "slices/project-managment/project-forecast/thunk"
import ProjectStatus from "models/project-status"
import Branch from "models/branch"
import Client from "models/client"
import Employee from "models/employee"
import DataTableTemplate from "Components/templates/data-table-template"
import SearchProjectsForm from "Components/organisms/forms/search-projects-form"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import { formatCurrency } from "utils/tools"
import { mergeProjectFinacnes, ProjectForecastSliceState, setQuery, setSearchFields } from "slices/project-managment/project-forecast/reducer"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import SearchSelect from "Components/molecule/inputs/search-select"
import ClientProjectFinance from "models/project-finance"
import { toast } from "react-toastify"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import CurrencyInput from "Components/molecule/inputs/currency-input"
import { useDiffStore } from "hooks/useInputDiffStore"
import { ProfileSliceState } from "slices/auth/profile/reducer"
import SyncButton from "Components/molecule/sync-button"
import { SyncLogSliceState } from "slices/sync-log/reducer"
import { SyncLogTypes } from "models/sync-log"
import { useNavigationGuard } from "../../../context/navigation-guard-provider";
import useManual from "hooks/useManual"
import useSyncProcess from "hooks/useSyncProcess"
import InfoTip from "Components/atoms/info-tip"
import { compareText } from "utils/department-options"
import ActionButton from "Components/atoms/buttons/action-button"
import HelpDocument, { HelpDocumentCode } from "models/help-document"
import useFiscalYearOptions from "hooks/useFiscalYearOptions"
import { HelpIcon } from "Components/atoms/icons"
import { toDepartmentOptions } from "utils/department-options"

const ACTIVE_PROJECT_STATUS = 'Active'
const PROJECT_FORECAST_CHANGES = 'project_forecast_changes'
const DIRTY_INPUT_CLASS = 'forecast-input--dirty'

const PRIVILEGED_ROLE = "Admin"
const PRIVILEGED_POSITION_TYPE = "Exec-Director"
const PRIVILEGED_JOB_TITLE = "President"


const DEFAULT_FISCAL_YEAR = FiscalCalendar.lastMonthFiscalYear();
const FORECAST_CONTROLS_ID = "forecast-controls"


const selectProjectForecast = createSelector(
    (state: any) => state.ProjectForecast,
    ({
        projects,
        projectCount,
        query,
        projectStatus,
        departments,
        branches,
        customers,
        projectManagers,
    }: ProjectForecastSliceState) => ({
        projects,
        projectCount,
        query,
        projectStatus,
        departments,
        branches,
        customers,
        projectManagers,
    })
)

const selectProfile = createSelector(
    (state: any) => state.Profile,
    ({
        roles,
        jobTitle,
    }: ProfileSliceState) => ({
        roles,
        jobTitle
    })
)

const selectSyncLog = createSelector(
    (state: any) => state.SyncLog,
    ({ syncLogs }: SyncLogSliceState) => ({
        syncLogs
    })
)


const ProjectForecastPage = (props: any) => {
    document.title = "Project WIH Forecast | IbexIQ";
    const { handleOpenManual } = useManual("project-managment/forecast");
    const [fiscalYear, setFiscalYear] = useState<number>(DEFAULT_FISCAL_YEAR)
    const { options: fiscalYearOptions, defaultYear, isLoading: isLoadingFiscalYears } = useFiscalYearOptions()

    // If the current fiscal year is hidden, move to the newest visible year (only if the user hasn't picked one).
    useEffect(() => {
        if (!isLoadingFiscalYears && defaultYear !== DEFAULT_FISCAL_YEAR && fiscalYear === DEFAULT_FISCAL_YEAR) {
            setFiscalYear(defaultYear)
        }
    }, [isLoadingFiscalYears, defaultYear])
    const [isLoadingProjects, setIsLoadingProjects] = useState<boolean>(true)
    const [isSavingForecast, setIsSavingForecast] = useState<boolean>(false)
    const { setIsDirty } = useNavigationGuard();
    const [exporting, setExporting] = useState<boolean>(false)
    const [showLeaveModal, setShowLeaveModal] = useState<boolean>(false);
    const dispatch: any = useDispatch();
    const {
        addChange,
        removeChange,
        getStoredChanges,
        getValue,
        commitChanges,
    } = useDiffStore<number>({
        storageKey: PROJECT_FORECAST_CHANGES,
        dirtyClass: DIRTY_INPUT_CLASS
    })
    const calendar = new FiscalCalendar(DEFAULT_FISCAL_START);

    const {
        projectCount,
        projects,
        query,
        projectStatus,
        departments,
        branches,
        customers,
        projectManagers,
    } = useSelector(selectProjectForecast);

    const {
        roles,
        jobTitle
    } = useSelector(selectProfile)

    const {
        syncLogs
    } = useSelector(selectSyncLog)



    const handleChangeQuery = async (
        key: string,
        value: any
    ) => {
        const q = { ...query }

        if (key !== 'page' && key !== 'limit') {
            q.page = 0;
            q.limit = Number.MAX_SAFE_INTEGER
        }

        q[key] = value

        dispatch(setQuery(q));
    };

    const handleExportToCSV = useCallback(() => {
        setExporting(true)
        try {
            downloadProjectForecastCSV(projects, fiscalYear, query, departments)
        } finally {
            setExporting(false)
        }
    }, [projects, fiscalYear, query, departments])

    const initializePage = async () => {
        // if we dont have any of the search fields lets get them
        let s = projectStatus;
        if (
            projectStatus.length === 0 ||
            departments.length === 0
        ) {
            const res = await getProjectForecastSearchFields()
            if (res) {
                dispatch(setSearchFields(res))
                s = res.projectStatus;
            }
        }

        if (!syncLogs[SyncLogTypes.PROJECTS])
            await dispatch(getMostRecentSyncLog(SyncLogTypes.PROJECTS))

        const activeStatus = s.find((s: ProjectStatus) => s.description === ACTIVE_PROJECT_STATUS);
        const q = { ...query }
        if (activeStatus) q.statusIds = [activeStatus.id]
        dispatch(setQuery(q))

        if (projects.length === 0) {
            await dispatch(getProjectForecast(q))
        }

        setIsLoadingProjects(false)
    }

    // Load Projects + Search fields
    useEffect(() => {
        initializePage()

        localStorage.removeItem(PROJECT_FORECAST_CHANGES)

        return () => localStorage.removeItem(PROJECT_FORECAST_CHANGES);
    }, [])

    useEffect(() => {
        if (projects.length === 0) return;

        const timeout = setTimeout(calculateTotalRow)

        return () => clearTimeout(timeout)


    }, [fiscalYear, projects]);

    const calculateTotalRow = () => {

        const sumInput = (inputs: NodeListOf<Element>): number => {
            let total = 0;

            inputs.forEach(el => {
                const input = el as HTMLInputElement;

                const raw = input.value?.replace(/[^0-9.-]/g, "") ?? "0";
                total += Number(raw);
            })

            return total
        }


        // ---- TOTAL VALUE COLUMN ----
        const budgetCells = document.querySelectorAll(".budget");
        const revenueCells = document.querySelectorAll(`.revenue`)
        const periodOneCells = document.querySelectorAll(`.period-1-${fiscalYear}`)
        const periodTwoCells = document.querySelectorAll(`.period-2-${fiscalYear}`)
        const periodThreeCells = document.querySelectorAll(`.period-3-${fiscalYear}`)
        const periodFourCells = document.querySelectorAll(`.period-4-${fiscalYear}`)
        const periodFiveCells = document.querySelectorAll(`.period-5-${fiscalYear}`)
        const periodSixCells = document.querySelectorAll(`.period-6-${fiscalYear}`)
        const periodSevenCells = document.querySelectorAll(`.period-7-${fiscalYear}`)
        const periodEightCells = document.querySelectorAll(`.period-8-${fiscalYear}`)
        const periodNineCells = document.querySelectorAll(`.period-9-${fiscalYear}`)
        const periodTenCells = document.querySelectorAll(`.period-10-${fiscalYear}`)
        const periodElevenCells = document.querySelectorAll(`.period-11-${fiscalYear}`)
        const periodTwelveCells = document.querySelectorAll(`.period-12-${fiscalYear}`)
        const varianceCells = document.querySelectorAll('.total-variance');

        // ---- FISCAL YEAR PORTION COLUMN ----
        const totalBudgetSummaryCell = document.querySelector(".budget-summary");
        const revenueSummaryCell = document.querySelector(".revenue-summary");
        const periodOneSummary = document.querySelector(`.period-1-${fiscalYear}-summary`)
        const periodTwoSummary = document.querySelector(`.period-2-${fiscalYear}-summary`)
        const periodThreeSummary = document.querySelector(`.period-3-${fiscalYear}-summary`)
        const periodFourSummary = document.querySelector(`.period-4-${fiscalYear}-summary`)
        const periodFiveSummary = document.querySelector(`.period-5-${fiscalYear}-summary`)
        const periodSixSummary = document.querySelector(`.period-6-${fiscalYear}-summary`)
        const periodSevenSummary = document.querySelector(`.period-7-${fiscalYear}-summary`)
        const periodEightSummary = document.querySelector(`.period-8-${fiscalYear}-summary`)
        const periodNineSummary = document.querySelector(`.period-9-${fiscalYear}-summary`)
        const periodTenSummary = document.querySelector(`.period-10-${fiscalYear}-summary`)
        const periodElevenSummary = document.querySelector(`.period-11-${fiscalYear}-summary`)
        const periodTwelveSummary = document.querySelector(`.period-12-${fiscalYear}-summary`)
        const totalVariance = document.querySelector(`.total-variance-summary`)

        // Total value
        let totalBudget = 0;
        budgetCells.forEach(cell => {
            const raw = cell.textContent?.replace(/[^0-9.-]/g, "");
            totalBudget += Number(raw ?? 0);
        });

        if (totalBudgetSummaryCell) {
            totalBudgetSummaryCell.innerHTML = formatCurrency(totalBudget);
        }

        // Total Forecast
        let totalVarianceSum = 0;
        varianceCells.forEach(cell => {
            const raw = cell.textContent?.replace(/[^0-9.-]/g, "");
            totalVarianceSum += Number(raw ?? 0);
        });

        if (totalVariance)
            totalVariance.innerHTML = formatCurrency(totalVarianceSum);

        let totalRevenueSum = 0
        revenueCells.forEach(cell => {
            const raw = cell.textContent?.replace(/[^0-9.-]/g, "");
            totalRevenueSum += Number(raw ?? 0);
        });

        if (revenueSummaryCell)
            revenueSummaryCell.innerHTML = formatCurrency(totalRevenueSum);

        if (periodOneSummary)
            periodOneSummary.innerHTML = formatCurrency(sumInput(periodOneCells));

        if (periodTwoSummary)
            periodTwoSummary.innerHTML = formatCurrency(sumInput(periodTwoCells));


        if (periodThreeSummary)
            periodThreeSummary.innerHTML = formatCurrency(sumInput(periodThreeCells));


        if (periodFourSummary)
            periodFourSummary.innerHTML = formatCurrency(sumInput(periodFourCells));


        if (periodFiveSummary)
            periodFiveSummary.innerHTML = formatCurrency(sumInput(periodFiveCells));


        if (periodSixSummary)
            periodSixSummary.innerHTML = formatCurrency(sumInput(periodSixCells));


        if (periodSevenSummary)
            periodSevenSummary.innerHTML = formatCurrency(sumInput(periodSevenCells));


        if (periodEightSummary)
            periodEightSummary.innerHTML = formatCurrency(sumInput(periodEightCells));


        if (periodNineSummary)
            periodNineSummary.innerHTML = formatCurrency(sumInput(periodNineCells));


        if (periodTenSummary)
            periodTenSummary.innerHTML = formatCurrency(sumInput(periodTenCells));


        if (periodElevenSummary)
            periodElevenSummary.innerHTML = formatCurrency(sumInput(periodElevenCells));


        if (periodTwelveSummary)
            periodTwelveSummary.innerHTML = formatCurrency(sumInput(periodTwelveCells));


    }

    const calculateProjectVariance = (projectId: string, storedChanges: any = {}) => {

        const project = projects.find(p => p.id === projectId);
        if (!project) return;

        let forecastTotal = 0;

        // All project finances
        project.projectFinances.forEach(pf => {
            let amount = Number(pf.forecastAmount ?? 0);
            const key = `${projectId}+${pf.fiscalPeriod}`

            if (storedChanges[key] !== undefined) {
                return;
            }

            forecastTotal += amount;
        });

        const changes = Object.keys(storedChanges)
        changes.forEach(c => {
            const [pId] = c.split("+");

            if (pId === projectId) {
                forecastTotal += storedChanges[c];
            }
        })

        const variance = project.budget - forecastTotal;

        const varianceCell = document.querySelector(
            `.total-variance-${projectId}`
        ) as HTMLElement | null;

        if (varianceCell) {
            varianceCell.innerHTML = formatCurrency(variance);
            varianceCell.classList.remove("error", "success");
            varianceCell.classList.add(variance < 0 ? "error" : "success");
        }
    };

    const handleClickProject = (project: ProjectForecast) => {
        const url = `/project-managment/projects/project?projectId=${project.id}`
        window.open(url, "_blank", "noopener,noreferrer")
    }

    const handleShowTableControls = () => {
        const controlsDiv = document.getElementById(FORECAST_CONTROLS_ID);
        if (controlsDiv)
            controlsDiv.style.display = 'block'
    }

    const handleHideTableControls = () => {
        const controlsDiv = document.getElementById(FORECAST_CONTROLS_ID);
        if (controlsDiv)
            controlsDiv.style.display = 'none'

    }

    const handleAddChange = (
        projectId: string,
        fiscalPeriod: number,
        amount: number
    ) => {
        if (amount < 0) {
            toast.error("Value must be positive");
            return;
        }

        const inputId = `${projectId}+${fiscalPeriod}`;

        const storedChanges = addChange(inputId, amount);
        setIsDirty(true);

        // Show controls
        handleShowTableControls()
        calculateTotalRow()
        calculateProjectVariance(projectId, storedChanges)
    };


    const handleReset = () => {
        const storedChanges = getStoredChanges();

        const keys = Object.keys(storedChanges);
        for (let i = 0; i < keys.length; i++) {
            const [projectId, fiscalPeriodStr] = keys[i].split("+");
            let newValue = 0;
            const project = projects.find(p => p.id === projectId);
            if (project) {
                const pF = project.projectFinances.find(pF => pF.fiscalPeriod === parseFloat(fiscalPeriodStr));
                newValue = pF ? pF.forecastAmount : 0;
            }
            removeChange(keys[i], "$" + newValue)
            calculateProjectVariance(projectId)
        }

        handleHideTableControls()
        calculateTotalRow()
        setIsDirty(false);
    }


    const handleSave = async () => {

        const storedChanges = getStoredChanges();

        setIsSavingForecast(true)

        const keys = Object.keys(storedChanges);
        const pFS: Pick<ClientProjectFinance, 'projectId' | 'fiscalPeriod' | 'plannedAmount' | 'forecastAmount'>[] = []
        // From the changes made, develop a list of projectFinance objects
        for (let i = 0; i < keys.length; i++) {
            const [projectId, fiscalPeriodStr] = keys[i].split("+");

            const project = projects.find(p => p.id === projectId);
            const pF = project?.projectFinances.find(pF => pF.fiscalPeriod === parseFloat(fiscalPeriodStr));

            const value = storedChanges[keys[i]];
            const newProjectFinance = {
                projectId: projectId,
                fiscalPeriod: parseFloat(fiscalPeriodStr),
                plannedAmount: pF?.plannedAmount ? pF.plannedAmount : 0,
                forecastAmount: value
            }

            pFS.push(newProjectFinance)
        }

        // Send them to the api to update
        const res = await updateProjectFinance(pFS);

        if (res) {
            await dispatch(mergeProjectFinacnes(res))

            // Commit
            commitChanges();
            setIsDirty(false);
            handleHideTableControls()
        }


        setIsSavingForecast(false)
    }

    const handleRemoveChange = (
        projectId: string,
        fiscalPeriod: number
    ) => {
        const key = `${projectId}+${fiscalPeriod}`;

        let newValue = 0;
        const project = projects.find(p => p.id === projectId);
        if (project) {
            const pF = project.projectFinances.find(pF => pF.fiscalPeriod === fiscalPeriod);
            newValue = pF ? pF.forecastAmount : 0;
        }

        const updatedChanges = removeChange(key, "$" + newValue);

        // If updated changes are none hide controls
        if (Object.keys(updatedChanges).length === 0) {
            setIsDirty(false);
            handleHideTableControls()
        }

        calculateTotalRow()
        calculateProjectVariance(projectId, updatedChanges)
    };


    const handleChangeFiscalYear = (year: number) => {
        setFiscalYear(year)
    }

    const handleSearch = useCallback(async () => {
        setIsLoadingProjects(true);
        await dispatch(getProjectForecast(query));
        setIsLoadingProjects(false)
    }, [query, getProjectForecast, setIsLoadingProjects])

    const columns = React.useMemo<ColumnDef<ProjectForecast>[]>(() => [
        {
            header: "Project",
            accessorKey: "code",
            size: 225,
            footer: ({ table }) => {

                if (table.getRowModel().rows.length === 0) return null
                return <div
                    style={{
                        textAlign: 'left',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                    Total Projects
                </div>
            },
            meta: {
                getCellStyle: (value: number) => {
                    return {
                        padding: '8px 12px',
                    }
                },
                getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            cell: ({ row }) => (
                <a
                    style={{
                        display: "block",
                        maxWidth: "100%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: "var(--bs-table-link-color)",
                        cursor: "pointer",
                    }}
                    onClick={() => handleClickProject(row.original)}
                >
                    {row.original.code || ""}
                </a>
            ),
        },
        {
            header: () => <span className="d-inline-flex align-items-center gap-1">
                Budget <InfoTip id="forecast-budget" text="The project's total revenue budget: the revised amounts of its revenue budget lines in Acumatica (synced on a schedule or from the project's details page)." />
            </span>,
            id: "budget",
            footer: ({ table }) => {
                if (table.getRowModel().rows.length === 0) return null

                return <div
                    className={`budget-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>
            },
            size: 110,
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: (value: number) => {
                    return {
                        textAlign: 'right',
                        padding: '0px',
                    }
                },
                getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            cell: ({ row }) => <div
                className={`budget`}
                style={{
                    textAlign: 'right',
                    backgroundColor: 'var(--bs-forecast-budget-bg)',
                    color: 'var(--bs-emphasis-color)',
                    padding: '8px 12px',
                }}>
                {formatCurrency(row.original.budget)}
            </div>
        },
        {
            header: () => <span className="d-inline-flex align-items-center gap-1">
                Revenue <InfoTip id="forecast-revenue" text="Revenue posted to the project in Acumatica so far, over its whole life: consulting income plus accrued revenue from the general ledger." />
            </span>,
            id: "revenue",
            footer: ({ table }) => {
                if (table.getRowModel().rows.length === 0) return null

                return <div
                    className={`revenue-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>
            },
            size: 110,
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: (value: number) => {
                    return {
                        textAlign: 'right',
                        padding: '0px',
                    }
                },
                getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            cell: ({ row }) => <div
                className={`revenue`}
                style={{
                    textAlign: 'right',
                    backgroundColor: 'var(--bs-forecast-revenue-bg)',
                    color: 'var(--bs-emphasis-color)',
                    padding: '8px 12px',
                }}>
                {formatCurrency(row.original.revenue)}
            </div>
        },
        {
            header: `${calendar.fiscalToLabel(fiscalYear, 1)}`,
            id: `1-${fiscalYear}`,
            size: 90,
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: () => {
                    return {
                        padding: 0,
                        textAlign: 'right',
                    }
                },
                getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            footer: () => {
                if (table.getRowModel().rows.length === 0) return null

                return <div
                    className={`period-1-${fiscalYear}-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>
            },
            cell: ({ row }) => {
                const PERIOD = 1;
                const inputId = `${row.original.id}+${fiscalYear * 100 + PERIOD}`;
                const pF = row.original.projectFinances.find(
                    p => p.fiscalPeriod === fiscalYear * 100 + PERIOD
                );
                const changedValue = getValue(inputId)
                const value =
                    changedValue !== undefined
                        ? changedValue
                        : Number(pF?.forecastAmount ?? 0);

                return (
                    <CurrencyInput
                        defaultValue={"$" + value}
                        id={inputId}
                        inputClassName={`period-${PERIOD}-${fiscalYear} forecast-input ${changedValue !== undefined ? DIRTY_INPUT_CLASS : ''}`}
                        onCommit={(newValue) => {
                            if (newValue === Number(pF?.forecastAmount ?? 0)) {
                                handleRemoveChange(row.original.id, fiscalYear * 100 + PERIOD);
                            } else {
                                handleAddChange(
                                    row.original.id,
                                    fiscalYear * 100 + PERIOD,
                                    newValue ?? 0
                                );
                            }
                        }}
                    />
                );
            }

        },
        {
            header: `${calendar.fiscalToLabel(fiscalYear, 2)}`,
            id: `2-${fiscalYear}`,
            size: 90,
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: (value: number) => {
                    return {
                        padding: 0,
                        textAlign: 'right'
                    }
                },
                getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            footer: () => {
                if (table.getRowModel().rows.length === 0) return null
                return <div
                    className={`period-2-${fiscalYear}-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>
            },
            cell: ({ row }) => {
                const PERIOD = 2;
                const inputId = `${row.original.id}+${fiscalYear * 100 + PERIOD}`;
                const pF = row.original.projectFinances.find(
                    p => p.fiscalPeriod === fiscalYear * 100 + PERIOD
                );
                const changedValue = getValue(inputId)
                const value =
                    changedValue !== undefined
                        ? changedValue
                        : Number(pF?.forecastAmount ?? 0);

                return (
                    <CurrencyInput
                        defaultValue={"$" + value}
                        id={inputId}
                        inputClassName={`period-${PERIOD}-${fiscalYear} forecast-input ${changedValue !== undefined ? DIRTY_INPUT_CLASS : ''}`}
                        onCommit={(newValue) => {
                            if (newValue === Number(pF?.forecastAmount ?? 0)) {
                                handleRemoveChange(row.original.id, fiscalYear * 100 + PERIOD);
                            } else {
                                handleAddChange(
                                    row.original.id,
                                    fiscalYear * 100 + PERIOD,
                                    newValue ?? 0
                                );
                            }
                        }}
                    />
                );
            }
        },
        {
            header: `${calendar.fiscalToLabel(fiscalYear, 3)}`,
            id: `3-${fiscalYear}`,
            size: 90,
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: (value: number) => {
                    return {
                        padding: 0,
                        textAlign: 'right'
                    }
                }, getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            footer: () => {
                if (table.getRowModel().rows.length === 0) return null
                return <div
                    className={`period-3-${fiscalYear}-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>
            },
            cell: ({ row }) => {
                const PERIOD = 3;
                const inputId = `${row.original.id}+${fiscalYear * 100 + PERIOD}`;
                const pF = row.original.projectFinances.find(
                    p => p.fiscalPeriod === fiscalYear * 100 + PERIOD
                );
                const changedValue = getValue(inputId)
                const value =
                    changedValue !== undefined
                        ? changedValue
                        : Number(pF?.forecastAmount ?? 0);

                return (
                    <CurrencyInput
                        defaultValue={"$" + value}
                        id={inputId}
                        inputClassName={`period-${PERIOD}-${fiscalYear} forecast-input ${changedValue !== undefined ? DIRTY_INPUT_CLASS : ''}`}
                        onCommit={(newValue) => {
                            if (newValue === Number(pF?.forecastAmount ?? 0)) {
                                handleRemoveChange(row.original.id, fiscalYear * 100 + PERIOD);
                            } else {
                                handleAddChange(
                                    row.original.id,
                                    fiscalYear * 100 + PERIOD,
                                    newValue ?? 0
                                );
                            }
                        }}
                    />
                );
            }
        },
        {
            header: `${calendar.fiscalToLabel(fiscalYear, 4)}`,
            id: `4-${fiscalYear}`,
            size: 90,
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: (value: number) => {
                    return {
                        padding: 0,
                        textAlign: 'right'
                    }
                }, getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            footer: () => {
                if (table.getRowModel().rows.length === 0) return null

                return <div
                    className={`period-4-${fiscalYear}-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>
            },
            cell: ({ row }) => {
                const PERIOD = 4;
                const inputId = `${row.original.id}+${fiscalYear * 100 + PERIOD}`;
                const pF = row.original.projectFinances.find(
                    p => p.fiscalPeriod === fiscalYear * 100 + PERIOD
                );
                const changedValue = getValue(inputId)
                const value =
                    changedValue !== undefined
                        ? changedValue
                        : Number(pF?.forecastAmount ?? 0);

                return (
                    <CurrencyInput
                        defaultValue={"$" + value}
                        id={inputId}
                        inputClassName={`period-${PERIOD}-${fiscalYear} forecast-input ${changedValue !== undefined ? DIRTY_INPUT_CLASS : ''}`}
                        onCommit={(newValue) => {
                            if (newValue === Number(pF?.forecastAmount ?? 0)) {
                                handleRemoveChange(row.original.id, fiscalYear * 100 + PERIOD);
                            } else {
                                handleAddChange(
                                    row.original.id,
                                    fiscalYear * 100 + PERIOD,
                                    newValue ?? 0
                                );
                            }
                        }}
                    />
                );
            }
        },
        {
            header: `${calendar.fiscalToLabel(fiscalYear, 5)}`,
            id: `5-${fiscalYear}`,
            size: 90,
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: (value: number) => {
                    return {
                        padding: 0,
                        textAlign: 'right',
                    }
                }, getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            footer: () => {
                if (table.getRowModel().rows.length === 0) return null

                return <div
                    className={`period-5-${fiscalYear}-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>
            },
            cell: ({ row }) => {
                const PERIOD = 5;
                const inputId = `${row.original.id}+${fiscalYear * 100 + PERIOD}`;
                const pF = row.original.projectFinances.find(
                    p => p.fiscalPeriod === fiscalYear * 100 + PERIOD
                );
                const changedValue = getValue(inputId)
                const value =
                    changedValue !== undefined
                        ? changedValue
                        : Number(pF?.forecastAmount ?? 0);

                return (
                    <CurrencyInput
                        defaultValue={"$" + value}
                        id={inputId}
                        inputClassName={`period-${PERIOD}-${fiscalYear} forecast-input ${changedValue !== undefined ? DIRTY_INPUT_CLASS : ''}`}
                        onCommit={(newValue) => {
                            if (newValue === Number(pF?.forecastAmount ?? 0)) {
                                handleRemoveChange(row.original.id, fiscalYear * 100 + PERIOD);
                            } else {
                                handleAddChange(
                                    row.original.id,
                                    fiscalYear * 100 + PERIOD,
                                    newValue ?? 0
                                );
                            }
                        }}
                    />
                );
            }
        },
        {
            header: `${calendar.fiscalToLabel(fiscalYear, 6)}`,
            id: `6-${fiscalYear}`,
            size: 90,
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: (value: number) => {
                    return {
                        padding: 0,
                        textAlign: 'right'
                    }
                }, getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            footer: () => {
                if (table.getRowModel().rows.length === 0) return null

                return <div
                    className={`period-6-${fiscalYear}-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>
            },
            cell: ({ row }) => {
                const PERIOD = 6;
                const inputId = `${row.original.id}+${fiscalYear * 100 + PERIOD}`;
                const pF = row.original.projectFinances.find(
                    p => p.fiscalPeriod === fiscalYear * 100 + PERIOD
                );
                const changedValue = getValue(inputId)
                const value =
                    changedValue !== undefined
                        ? changedValue
                        : Number(pF?.forecastAmount ?? 0);

                return (
                    <CurrencyInput
                        defaultValue={"$" + value}
                        id={inputId}
                        inputClassName={`period-${PERIOD}-${fiscalYear} forecast-input ${changedValue !== undefined ? DIRTY_INPUT_CLASS : ''}`}
                        onCommit={(newValue) => {
                            if (newValue === Number(pF?.forecastAmount ?? 0)) {
                                handleRemoveChange(row.original.id, fiscalYear * 100 + PERIOD);
                            } else {
                                handleAddChange(
                                    row.original.id,
                                    fiscalYear * 100 + PERIOD,
                                    newValue ?? 0
                                );
                            }
                        }}
                    />
                );
            }
        },
        {
            header: `${calendar.fiscalToLabel(fiscalYear, 7)}`,
            id: `7-${fiscalYear}`,
            size: 90,
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: (value: number) => {
                    return {
                        padding: 0,
                        textAlign: 'right'
                    }
                }, getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            footer: () => {
                if (table.getRowModel().rows.length === 0) return null

                return <div
                    className={`period-7-${fiscalYear}-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>
            },
            cell: ({ row }) => {
                const PERIOD = 7;
                const inputId = `${row.original.id}+${fiscalYear * 100 + PERIOD}`;
                const pF = row.original.projectFinances.find(
                    p => p.fiscalPeriod === fiscalYear * 100 + PERIOD
                );
                const changedValue = getValue(inputId)
                const value =
                    changedValue !== undefined
                        ? changedValue
                        : Number(pF?.forecastAmount ?? 0);

                return (
                    <CurrencyInput
                        defaultValue={"$" + value}
                        id={inputId}
                        inputClassName={`period-${PERIOD}-${fiscalYear} forecast-input ${changedValue !== undefined ? DIRTY_INPUT_CLASS : ''}`}
                        onCommit={(newValue) => {
                            if (newValue === Number(pF?.forecastAmount ?? 0)) {
                                handleRemoveChange(row.original.id, fiscalYear * 100 + PERIOD);
                            } else {
                                handleAddChange(
                                    row.original.id,
                                    fiscalYear * 100 + PERIOD,
                                    newValue ?? 0
                                );
                            }
                        }}
                    />
                );
            }
        },
        {
            header: `${calendar.fiscalToLabel(fiscalYear, 8)}`,
            id: `8-${fiscalYear}`,
            size: 90,
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: (value: number) => {
                    return {
                        padding: 0,
                        textAlign: 'right'
                    }
                },
                getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            footer: () => {
                if (table.getRowModel().rows.length === 0) return null

                return <div
                    className={`period-8-${fiscalYear}-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>

            },
            cell: ({ row }) => {
                const PERIOD = 8;
                const inputId = `${row.original.id}+${fiscalYear * 100 + PERIOD}`;
                const pF = row.original.projectFinances.find(
                    p => p.fiscalPeriod === fiscalYear * 100 + PERIOD
                );
                const changedValue = getValue(inputId)
                const value =
                    changedValue !== undefined
                        ? changedValue
                        : Number(pF?.forecastAmount ?? 0);

                return (
                    <CurrencyInput
                        defaultValue={"$" + value}
                        id={inputId}
                        inputClassName={`period-${PERIOD}-${fiscalYear} forecast-input ${changedValue !== undefined ? DIRTY_INPUT_CLASS : ''}`}
                        onCommit={(newValue) => {
                            if (newValue === Number(pF?.forecastAmount ?? 0)) {
                                handleRemoveChange(row.original.id, fiscalYear * 100 + PERIOD);
                            } else {
                                handleAddChange(
                                    row.original.id,
                                    fiscalYear * 100 + PERIOD,
                                    newValue ?? 0
                                );
                            }
                        }}
                    />
                );
            }
        },
        {
            header: `${calendar.fiscalToLabel(fiscalYear, 9)}`,
            id: `9-${fiscalYear}`,
            size: 90,
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: (value: number) => {
                    return {
                        padding: 0,
                        textAlign: 'right'
                    }
                },
                getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            footer: () => {
                if (table.getRowModel().rows.length === 0) return null

                return <div
                    className={`period-9-${fiscalYear}-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>
            },
            cell: ({ row }) => {
                const PERIOD = 9;
                const inputId = `${row.original.id}+${fiscalYear * 100 + PERIOD}`;
                const pF = row.original.projectFinances.find(
                    p => p.fiscalPeriod === fiscalYear * 100 + PERIOD
                );
                const changedValue = getValue(inputId)
                const value =
                    changedValue !== undefined
                        ? changedValue
                        : Number(pF?.forecastAmount ?? 0);

                return (
                    <CurrencyInput
                        defaultValue={"$" + value}
                        id={inputId}
                        inputClassName={`period-${PERIOD}-${fiscalYear} forecast-input ${changedValue !== undefined ? DIRTY_INPUT_CLASS : ''}`}
                        onCommit={(newValue) => {
                            if (newValue === Number(pF?.forecastAmount ?? 0)) {
                                handleRemoveChange(row.original.id, fiscalYear * 100 + PERIOD);
                            } else {
                                handleAddChange(
                                    row.original.id,
                                    fiscalYear * 100 + PERIOD,
                                    newValue ?? 0
                                );
                            }
                        }}
                    />
                );
            }
        },
        {
            header: `${calendar.fiscalToLabel(fiscalYear, 10)}`,
            id: `10-${fiscalYear}`,
            size: 90,
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: (value: number) => {
                    return {
                        padding: 0,
                        textAlign: 'right'
                    }
                },
                getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            footer: () => {
                if (table.getRowModel().rows.length === 0) return null

                return <div
                    className={`period-10-${fiscalYear}-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>
            },
            cell: ({ row }) => {
                const PERIOD = 10;
                const inputId = `${row.original.id}+${fiscalYear * 100 + PERIOD}`;
                const pF = row.original.projectFinances.find(
                    p => p.fiscalPeriod === fiscalYear * 100 + PERIOD
                );
                const changedValue = getValue(inputId)
                const value =
                    changedValue !== undefined
                        ? changedValue
                        : Number(pF?.forecastAmount ?? 0);

                return (
                    <CurrencyInput
                        defaultValue={"$" + value}
                        id={inputId}
                        inputClassName={`period-${PERIOD}-${fiscalYear} forecast-input ${changedValue !== undefined ? DIRTY_INPUT_CLASS : ''}`}
                        onCommit={(newValue) => {
                            if (newValue === Number(pF?.forecastAmount ?? 0)) {
                                handleRemoveChange(row.original.id, fiscalYear * 100 + PERIOD);
                            } else {
                                handleAddChange(
                                    row.original.id,
                                    fiscalYear * 100 + PERIOD,
                                    newValue ?? 0
                                );
                            }
                        }}
                    />
                );
            }
        },
        {
            header: `${calendar.fiscalToLabel(fiscalYear, 11)}`,
            id: `11-${fiscalYear}`,
            size: 90,
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: (value: number) => {
                    return {
                        padding: 0,
                        textAlign: 'right'
                    }
                },
                getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            footer: () => {
                if (table.getRowModel().rows.length === 0) return null

                return <div
                    className={`period-11-${fiscalYear}-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>
            },
            cell: ({ row }) => {
                const PERIOD = 11;
                const inputId = `${row.original.id}+${fiscalYear * 100 + PERIOD}`;
                const pF = row.original.projectFinances.find(
                    p => p.fiscalPeriod === fiscalYear * 100 + PERIOD
                );
                const changedValue = getValue(inputId)
                const value =
                    changedValue !== undefined
                        ? changedValue
                        : Number(pF?.forecastAmount ?? 0);

                return (
                    <CurrencyInput
                        defaultValue={"$" + value}
                        id={inputId}
                        inputClassName={`period-${PERIOD}-${fiscalYear} forecast-input ${changedValue !== undefined ? DIRTY_INPUT_CLASS : ''}`}
                        onCommit={(newValue) => {
                            if (newValue === Number(pF?.forecastAmount ?? 0)) {
                                handleRemoveChange(row.original.id, fiscalYear * 100 + PERIOD);
                            } else {
                                handleAddChange(
                                    row.original.id,
                                    fiscalYear * 100 + PERIOD,
                                    newValue ?? 0
                                );
                            }
                        }}
                    />
                );
            }
        },
        {
            header: `${calendar.fiscalToLabel(fiscalYear, 12)}`,
            id: `12-${fiscalYear}`,
            size: 90,
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: (value: number) => {
                    return {
                        padding: 0,
                    }
                },
                getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            footer: () => {
                if (table.getRowModel().rows.length === 0) return null

                return <div
                    className={`period-12-${fiscalYear}-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>
            },
            cell: ({ row }) => {
                const PERIOD = 12;
                const inputId = `${row.original.id}+${fiscalYear * 100 + PERIOD}`;
                const pF = row.original.projectFinances.find(
                    p => p.fiscalPeriod === fiscalYear * 100 + PERIOD
                );
                const changedValue = getValue(inputId)
                const value =
                    changedValue !== undefined
                        ? changedValue
                        : Number(pF?.forecastAmount ?? 0);

                return (
                    <CurrencyInput
                        defaultValue={"$" + value}
                        id={inputId}
                        inputClassName={`period-${PERIOD}-${fiscalYear} forecast-input ${changedValue !== undefined ? DIRTY_INPUT_CLASS : ''}`}
                        onCommit={(newValue) => {
                            if (newValue === Number(pF?.forecastAmount ?? 0)) {
                                handleRemoveChange(row.original.id, fiscalYear * 100 + PERIOD);
                            } else {
                                handleAddChange(
                                    row.original.id,
                                    fiscalYear * 100 + PERIOD,
                                    newValue ?? 0
                                );
                            }
                        }}
                    />
                );
            }
        },
        {
            header: `Variance`,
            id: 'total-variance',
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                    padding: "8px 12px",
                }),
                getCellStyle: (value: number) => {
                    return {
                        padding: 0,
                    }
                },
                getFooterStyle: () => {
                    return {
                        padding: '0px'
                    }
                }
            },
            footer: () => {
                if (table.getRowModel().rows.length === 0) return null

                return <div
                    className={`total-variance-summary`}
                    style={{
                        textAlign: 'right',
                        backgroundColor: 'darkGreen',
                        color: 'white',
                        padding: '8px 12px',
                    }}>
                </div>

            },
            cell: ({ row }) => {
                const raw = localStorage.getItem(PROJECT_FORECAST_CHANGES);
                const storedChanges: Record<string, number> =
                    raw ? JSON.parse(raw) : {};

                let total = 0;

                // 1️⃣ Existing project finances (override with change if exists)
                row.original.projectFinances.forEach(pf => {
                    const key = `${row.original.id}+${pf.fiscalPeriod}`;

                    if (storedChanges[key] === undefined) {
                        total += Number(pf.forecastAmount ?? 0);
                    }
                });

                // 2️⃣ Add changes that don't exist in projectFinances
                Object.keys(storedChanges).forEach(key => {
                    const [projectId] = key.split("+");

                    if (projectId === row.original.id) {
                        total += storedChanges[key];
                    }
                });

                const variance = row.original.budget - total;

                return (
                    <div
                        className={`total-variance total-variance-${row.original.id} ${variance < 0 ? 'error' : 'success'}`}
                        style={{
                            textAlign: 'right',
                            padding: '8px 12px',
                        }}
                    >
                        {formatCurrency(variance)}
                    </div>
                );
            }
        }
    ], [fiscalYear, projects]);

    const handleClearSearch = useCallback(async () => {
        const activeStatus = projectStatus.find((s: ProjectStatus) => s.description === ACTIVE_PROJECT_STATUS);
        const q: IProjectQueryParam = {
            ...query,
            page: 0,
            limit: Number.MAX_SAFE_INTEGER,
            branchIds: [],
            clientIds: [],
            projectManagerIds: [],
            departmentIds: [],
            code: ''
        }
        if (activeStatus) q.statusIds = [activeStatus.id]

        dispatch(setQuery(q));
    }, [dispatch, setIsLoadingProjects, query, projectStatus]);

    // Forecast grid has DOM-computed totals: no sorting
    // Sorted by project code by default
    const sortedProjects = useMemo(
        () => [...projects].sort((a: any, b: any) => compareText(a.code ?? '', b.code ?? '')),
        [projects]
    )

    const table = useDataTable({
        data: sortedProjects,
        columns,
        enableSorting: false,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    });

    const handleOpenLeaveModal = () => setShowLeaveModal(true)
    const handleCloseLeaveModal = () => setShowLeaveModal(false)


    const { isSyncing: isSyncingData, startSync } = useSyncProcess({
        module: SyncLogTypes.PROJECTS,
        start: syncProjects,
        onFinished: handleSearch,
    })

    const handleSyncProjects = async () => {
        handleCloseLeaveModal();
        await startSync()
    }

    return <DataTableTemplate
        fullExport={handleExportToCSV}
        title={"Project"}
        breadcrumbItem={"Project WIH Forecast"}
        table={table}
        columns={columns}
        isLoading={isLoadingProjects}
        rowCount={projectCount}
        filters={
            <SearchProjectsForm
                handleClearSearch={handleClearSearch}
                query={query}
                handleChangeQuery={handleChangeQuery}
                branches={branches.map((b: Branch) => ({ value: b.id, label: b.name }))}
                projectManagers={projectManagers.map((emp: Employee) => ({ value: emp.id, label: `${emp.commonName ?? emp.firstName} ${emp.lastName}` }))}
                customers={customers.map((c: Client) => ({ value: c.id, label: c.name }))}
                status={projectStatus.map((pS: ProjectStatus) => ({ value: pS.id, label: pS.description }))}
                department={toDepartmentOptions(departments)}
                handleSearch={handleSearch}
                isSearching={isLoadingProjects}
            />}
        actions={<>
            <SyncButton
                id="projectsSyncBtn"
                isSyncing={isSyncingData}
                syncLog={syncLogs[SyncLogTypes.PROJECTS]}
                onClick={handleOpenLeaveModal}
            />
            {
                <ActionButton
                    onClick={handleOpenManual}
                >
                    Help <HelpIcon />
                </ActionButton>
            }
        </>}
        modal={<AreYouSureModal
            isOpen={showLeaveModal}
            onModalClose={handleCloseLeaveModal}
            handleAreYouSureDecline={async () => handleCloseLeaveModal()}
            handleAreYouSureAccept={handleSyncProjects}
            modalTitle="Sync Projects"
            subTitle={"Sync acumatica and ibexIQ projects"}
            areYouSureacceptText="Sync"
            areYouSuredeclineText="Cancel"
        />}
        tableActions={<>
            {
                <div id={FORECAST_CONTROLS_ID} style={{ display: 'none' }}>
                    <PrimaryButton
                        style={{ minWidth: '100px' }}
                        onClick={() => handleSave()}
                        disabled={isSavingForecast}
                    >
                        {isSavingForecast ? 'Saving...' : 'Save'}
                    </PrimaryButton>
                    <SecondaryButton
                        style={{
                            marginLeft: '10px'
                        }}
                        onClick={handleReset}
                    >
                        Reset
                    </SecondaryButton>
                </div>
            }
            <SearchSelect
                value={fiscalYear.toString()}
                onChange={(e) => handleChangeFiscalYear(e?.value ? parseFloat(e?.value) : defaultYear)}
                options={fiscalYearOptions}
                inputStyle={{
                    margin: 0,
                    minWidth: '150px'
                }}
            />
        </>}
    />
}


export default withRouter(ProjectForecastPage);